import React, { useState, useEffect } from 'react';
import { 
  Card, Typography, Space, Button, Table, Tag, Modal, Form, Input, Select, 
  DatePicker, Row, Col, Statistic, message, Popconfirm, Descriptions,
  Badge, Divider, Alert, Spin, Drawer, List, Avatar, Tabs, Switch,
  TimePicker, Calendar
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, ClockCircleOutlined, 
  CheckCircleOutlined, ExclamationCircleOutlined, CalendarOutlined, SettingOutlined,
  EyeOutlined, AlertOutlined, TeamOutlined, BarChartOutlined, FileTextOutlined,
  KeyOutlined, SafetyOutlined, CheckOutlined, CloseOutlined, SearchOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import studentAPI from '../../services/studentAPI';
import api from '../../services/api';
import moment from 'moment';
import { resolveOperationSchoolId } from '../../utils/schoolScope';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const ManualAttendance = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [codeForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [operatorCodes, setOperatorCodes] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({});
  const [recordModalVisible, setRecordModalVisible] = useState(false);
  const [codeModalVisible, setCodeModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));

  // 考勤状态选项
  const attendanceStatus = [
    { value: 'present', label: '出勤', color: 'green', icon: <CheckCircleOutlined /> },
    { value: 'absent', label: '缺勤', color: 'red', icon: <CloseOutlined /> },
    { value: 'late', label: '迟到', color: 'orange', icon: <ClockCircleOutlined /> },
    { value: 'leave', label: '请假', color: 'blue', icon: <FileTextOutlined /> },
    { value: 'sick_leave', label: '病假', color: 'purple', icon: <ExclamationCircleOutlined /> },
  ];

  // 时段选项
  const sessionTypes = [
    { value: 'morning', label: '上午', color: 'gold' },
    { value: 'afternoon', label: '下午', color: 'blue' },
    { value: 'evening', label: '晚上', color: 'purple' },
  ];

  // 操作员类型选项
  const operatorTypes = [
    { value: 'student', label: '学生', color: 'blue' },
    { value: 'monitor', label: '班长', color: 'green' },
    { value: 'duty', label: '值日生', color: 'orange' },
    { value: 'teacher', label: '教师', color: 'red' },
  ];

  // 获取考勤记录列表
  const fetchRecords = async (params = {}) => {
    setLoading(true);
    try {
      const schoolId = resolveOperationSchoolId({
        classes,
        classId: filters.classId || filters.class_id,
        user
      });
      const response = await studentAPI.attendance.getRecords({
        ...filters,
        page: pagination.current,
        pageSize: pagination.pageSize,
        schoolId: schoolId || undefined
      });
      
      if (response.data.success) {
        setRecords(response.data.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0
        }));
      }
    } catch (error) {
      message.error('获取考勤记录失败');
      console.error('获取考勤记录失败:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // 获取操作码列表
  const fetchOperatorCodes = async (classId) => {
    if (!classId) {
      setOperatorCodes([]);
      return;
    }
    try {
      const response = await api.get(`/attendance/codes/${classId}`);
      if (response.data.success) {
        setOperatorCodes(response.data.data || []);
        console.log('获取到的操作码列表:', response.data.data);
      }
    } catch (error) {
      console.error('获取操作码失败:', error);
      setOperatorCodes([]);
    }
  };

  // 获取学生列表（根据班级筛选）
  const fetchStudents = async (classId = null) => {
    if (!classId) {
      setStudents([]);
      return;
    }
    try {
      const params = { classId };
      const response = await api.get('/students', { params });
      if (response.data.success) {
        setStudents(response.data.data || []);
      }
    } catch (error) {
      console.error('获取学生列表失败:', error);
      setStudents([]);
    }
  };

  // 获取班级列表（教师只能看到自己的班级）
  const fetchClasses = async () => {
    try {
      const response = await api.get('/classes', { params: { schoolId: user?.workbenchSchoolId } });
      if (response.data.success) {
        setClasses(response.data.data || []);
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
      setClasses([]);
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [filters]);

  // 处理表格变化
  const handleTableChange = (newPagination, tableFilters, sorter) => {
    setPagination(newPagination);
  };

  // 处理筛选
  const handleFilter = (values) => {
    setFilters(values);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 重置筛选
  const resetFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 打开考勤记录模态框
  const openRecordModal = (record = null) => {
    setEditingRecord(record);
    setRecordModalVisible(true);
    
    if (record) {
      form.setFieldsValue({
        ...record,
        attendance_date: record.attendance_date ? moment(record.attendance_date) : moment(),
        attendance_time: record.attendance_time ? moment(record.attendance_time, 'HH:mm:ss') : moment(),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        attendance_date: moment(),
        attendance_time: moment(),
        session_type: 'morning',
        status: 'present'
      });
    }
  };

  // 关闭考勤记录模态框
  const closeRecordModal = () => {
    setRecordModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  // 打开操作码模态框
  const openCodeModal = (code = null) => {
    setEditingCode(code);
    setCodeModalVisible(true);
    
    if (code) {
      codeForm.setFieldsValue(code);
    } else {
      codeForm.resetFields();
      codeForm.setFieldsValue({
        class_id: selectedClass || classes[0]?.id,
        operator_type: 'student',
        is_active: true
      });
    }
  };

  // 关闭操作码模态框
  const closeCodeModal = () => {
    setCodeModalVisible(false);
    setEditingCode(null);
    codeForm.resetFields();
  };

  // 查看详情
  const viewDetail = (record) => {
    setSelectedRecord(record);
    setDetailDrawerVisible(true);
  };

  // 提交考勤记录
  const handleRecordSubmit = async (values) => {
    try {
      const schoolId = resolveOperationSchoolId({
        classes,
        classId: values.class_id || values.classId,
        item: editingRecord,
        user
      });
      const formData = {
        ...values,
        attendance_date: values.attendance_date.format('YYYY-MM-DD'),
        attendance_time: values.attendance_time.format('HH:mm:ss'),
        school_id: schoolId,
        // 教师/管理员添加记录，不需要操作码，直接记录操作人信息
        operator_name: user.name || user.username,
        created_by: user.id
      };

      if (editingRecord) {
        await api.put(`/attendance/records/${editingRecord.id}`, formData);
        message.success('考勤记录更新成功');
      } else {
        await api.post('/attendance/records', formData);
        message.success('考勤记录提交成功');
      }
      
      closeRecordModal();
      fetchRecords();
    } catch (error) {
      message.error(error.response?.data?.message || (editingRecord ? '更新失败' : '提交失败'));
      console.error('提交失败:', error);
    }
  };

  // 提交操作码
  const handleCodeSubmit = async (values) => {
    try {
      if (editingCode) {
        await api.put(`/attendance/codes/${editingCode.id}`, values);
        message.success('操作码更新成功');
      } else {
        await api.post('/attendance/codes', values);
        message.success('操作码创建成功');
      }
      closeCodeModal();
      // 重新加载当前选中班级的操作码
      if (values.class_id) {
        fetchOperatorCodes(values.class_id);
      }
    } catch (error) {
      message.error(error.response?.data?.message || (editingCode ? '更新失败' : '创建失败'));
      console.error('提交失败:', error);
    }
  };

  // 删除记录
  const handleDeleteRecord = async (id) => {
    try {
      // 这里应该调用删除API
      message.success('删除成功');
      fetchRecords();
    } catch (error) {
      message.error('删除失败');
      console.error('删除失败:', error);
    }
  };

  // 删除操作码
  const handleDeleteCode = async (id, classId) => {
    try {
      await api.delete(`/attendance/codes/${id}`);
      message.success('删除成功');
      // 重新加载该班级的操作码
      if (classId) {
        fetchOperatorCodes(classId);
      }
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
      console.error('删除失败:', error);
    }
  };

  // 获取状态标签
  const getStatusTag = (status) => {
    const statusOption = attendanceStatus.find(s => s.value === status);
    return (
      <Tag color={statusOption?.color} icon={statusOption?.icon}>
        {statusOption?.label}
      </Tag>
    );
  };

  // 获取时段标签
  const getSessionTag = (sessionType) => {
    const sessionOption = sessionTypes.find(s => s.value === sessionType);
    return <Tag color={sessionOption?.color}>{sessionOption?.label}</Tag>;
  };

  // 获取操作员类型标签
  const getOperatorTypeTag = (type) => {
    const typeOption = operatorTypes.find(t => t.value === type);
    return <Tag color={typeOption?.color}>{typeOption?.label}</Tag>;
  };

  // 考勤记录表格列
  const recordColumns = [
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 100,
      fixed: 'left',
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 120,
    },
    {
      title: '考勤日期',
      dataIndex: 'attendance_date',
      key: 'attendance_date',
      width: 100,
      render: (date) => moment(date).format('MM-DD'),
    },
    {
      title: '考勤时间',
      dataIndex: 'attendance_time',
      key: 'attendance_time',
      width: 100,
      render: (time) => moment(time, 'HH:mm:ss').format('HH:mm'),
    },
    {
      title: '时段',
      dataIndex: 'session_type',
      key: 'session_type',
      width: 80,
      render: (sessionType) => getSessionTag(sessionType),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: '操作员',
      dataIndex: 'operator_name',
      key: 'operator_name',
      width: 100,
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text>{name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.operator_code}
          </Text>
        </Space>
      ),
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => viewDetail(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openRecordModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条考勤记录吗？"
            onConfirm={() => handleDeleteRecord(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 操作码表格列
  const codeColumns = [
    {
      title: '操作码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (code) => <Text code>{code}</Text>,
    },
    {
      title: '操作员姓名',
      dataIndex: 'operator_name',
      key: 'operator_name',
      width: 120,
    },
    {
      title: '类型',
      dataIndex: 'operator_type',
      key: 'operator_type',
      width: 100,
      render: (type) => getOperatorTypeTag(type),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (isActive) => (
        <Badge 
          status={isActive ? 'success' : 'default'} 
          text={isActive ? '启用' : '禁用'} 
        />
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (time) => moment(time).format('MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openCodeModal(record)}
          >
            编辑
          </Button>
            <Popconfirm
              title="确定要删除这个操作码吗？"
              onConfirm={() => handleDeleteCode(record.id, record.class_id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 考勤统计 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="今日考勤记录"
              value={records.filter(r => r.attendance_date === moment().format('YYYY-MM-DD')).length}
              prefix={<CheckCircleOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
              suffix="条"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="出勤率"
              value={records.length > 0 ? (records.filter(r => r.status === 'present').length / records.length * 100) : 0}
              precision={1}
              prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
              suffix="%"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="迟到人次"
              value={records.filter(r => r.status === 'late').length}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
              suffix="次"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="缺勤人次"
              value={records.filter(r => r.status === 'absent').length}
              prefix={<ExclamationCircleOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
              suffix="次"
            />
          </Card>
        </Col>
      </Row>

      {/* 主要内容区域 */}
      <Tabs defaultActiveKey="records">
        <TabPane tab="考勤记录" key="records">
          {/* 筛选区域 */}
          <Card style={{ marginBottom: 16 }}>
            <Form layout="inline" onFinish={handleFilter}>
              <Form.Item name="classId" label="班级">
                <Select
                  placeholder="选择班级"
                  style={{ width: 150 }}
                  allowClear
                >
                  {classes.map(cls => (
                    <Option key={cls.id} value={cls.id}>
                      {cls.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="studentId" label="学生">
                <Select
                  placeholder="选择学生"
                  style={{ width: 150 }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {students.map(student => (
                    <Option key={student.id} value={student.id}>
                      {student.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="status" label="状态">
                <Select
                  placeholder="选择状态"
                  style={{ width: 120 }}
                  allowClear
                >
                  {attendanceStatus.map(status => (
                    <Option key={status.value} value={status.value}>
                      {getStatusTag(status.value)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="sessionType" label="时段">
                <Select
                  placeholder="选择时段"
                  style={{ width: 120 }}
                  allowClear
                >
                  {sessionTypes.map(session => (
                    <Option key={session.value} value={session.value}>
                      {getSessionTag(session.value)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="dateRange" label="日期">
                <RangePicker style={{ width: 240 }} />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                    筛选
                  </Button>
                  <Button onClick={resetFilters}>
                    重置
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>

          {/* 考勤记录表格 */}
          <Card title="考勤记录管理">
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openRecordModal()}
                >
                  新增记录
                </Button>
                <Button
                  icon={<CalendarOutlined />}
                  onClick={() => setCalendarVisible(true)}
                >
                  日历视图
                </Button>
              </Space>
              
              <Space>
                <Button icon={<BarChartOutlined />}>
                  统计报表
                </Button>
                <Button icon={<AlertOutlined />}>
                  异常提醒
                </Button>
              </Space>
            </div>

            <Table
              columns={recordColumns}
              dataSource={records}
              rowKey="id"
              loading={loading}
              pagination={pagination}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="操作码管理" key="codes">
          <Card title="班级操作码管理">
            <Alert
              message="操作码管理说明"
              description="操作码用于桌面客户端考勤打卡。教师可以为班级设置不同的操作码，分配给值日生、班长等学生使用。注意：教师和管理员在后台添加考勤记录时不需要操作码。"
              type="info"
              style={{ marginBottom: 16 }}
              showIcon
            />

            <Space style={{ marginBottom: '16px' }}>
              <Select
                placeholder="选择班级查看操作码"
                style={{ width: 200 }}
                onChange={(value) => {
                  setSelectedClass(value);
                  fetchOperatorCodes(value);
                }}
                value={selectedClass}
              >
                {classes.map(cls => (
                  <Option key={cls.id} value={cls.id}>
                    {cls.name}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openCodeModal()}
                disabled={!selectedClass}
              >
                新增操作码
              </Button>
            </Space>

            <Table
              columns={codeColumns}
              dataSource={operatorCodes}
              rowKey="id"
              pagination={false}
              locale={{ emptyText: '请先选择班级' }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* 考勤记录模态框 */}
      <Modal
        title={editingRecord ? '编辑考勤记录' : '新增考勤记录'}
        open={recordModalVisible}
        onCancel={closeRecordModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleRecordSubmit}
        >
          <Form.Item
            name="class_id"
            label="班级"
            rules={[{ required: true, message: '请先选择班级' }]}
          >
            <Select
              placeholder="请先选择班级"
              onChange={(value) => {
                // 清空学生选择
                form.setFieldsValue({ student_id: undefined, student_name: undefined });
                // 加载该班级的学生
                fetchStudents(value);
                // 加载该班级的操作码
                fetchOperatorCodes(value);
              }}
            >
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>
                  {cls.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="student_id"
            label="学生"
            rules={[{ required: true, message: '请选择学生' }]}
          >
            <Select
              placeholder="请先选择班级后再选择学生"
              showSearch
              optionFilterProp="children"
              disabled={!form.getFieldValue('class_id')}
              onChange={(value) => {
                const student = students.find(s => s.id === value);
                if (student) {
                  form.setFieldsValue({
                    student_name: student.student_name || student.name,
                  });
                }
              }}
            >
              {students.map(student => (
                <Option key={student.id} value={student.id}>
                  {student.student_name || student.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="student_name" style={{ display: 'none' }}>
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="attendance_date"
                label="考勤日期"
                rules={[{ required: true, message: '请选择考勤日期' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="attendance_time"
                label="考勤时间"
                rules={[{ required: true, message: '请选择考勤时间' }]}
              >
                <TimePicker style={{ width: '100%' }} format="HH:mm" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="session_type"
                label="时段"
                rules={[{ required: true, message: '请选择时段' }]}
              >
                <Select placeholder="选择时段">
                  {sessionTypes.map(session => (
                    <Option key={session.value} value={session.value}>
                      {getSessionTag(session.value)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="考勤状态"
                rules={[{ required: true, message: '请选择考勤状态' }]}
              >
                <Select placeholder="选择状态">
                  {attendanceStatus.map(status => (
                    <Option key={status.value} value={status.value}>
                      {getStatusTag(status.value)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 操作码字段 - 教师和管理员不需要填写，系统自动记录 */}
          <Form.Item name="operator_code" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Form.Item name="operator_name" style={{ display: 'none' }}>
            <Input />
          </Form.Item>

          <Form.Item name="remarks" label="备注">
            <Input.TextArea rows={3} placeholder="请输入备注信息" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRecord ? '更新' : '提交'}
              </Button>
              <Button onClick={closeRecordModal}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 操作码模态框 */}
      <Modal
        title={editingCode ? '编辑操作码' : '新增操作码'}
        open={codeModalVisible}
        onCancel={closeCodeModal}
        footer={null}
        width={500}
      >
        <Form
          form={codeForm}
          layout="vertical"
          onFinish={handleCodeSubmit}
        >
          <Form.Item
            name="class_id"
            label="班级"
            rules={[{ required: true, message: '请选择班级' }]}
          >
            <Select placeholder="选择班级">
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>
                  {cls.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="操作码"
                rules={[
                  { required: true, message: '请输入操作码' },
                  { pattern: /^[A-Z0-9]+$/, message: '操作码只能包含大写字母和数字' }
                ]}
              >
                <Input placeholder="例：DUTY001" maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="operator_type"
                label="操作员类型"
                rules={[{ required: true, message: '请选择操作员类型' }]}
              >
                <Select placeholder="选择类型">
                  {operatorTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      {getOperatorTypeTag(type.value)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="operator_name"
            label="操作员姓名"
            rules={[{ required: true, message: '请输入操作员姓名' }]}
          >
            <Input placeholder="请输入操作员姓名" />
          </Form.Item>

          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} placeholder="请输入操作码描述" />
          </Form.Item>

          <Form.Item name="is_active" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCode ? '更新' : '创建'}
              </Button>
              <Button onClick={closeCodeModal}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 考勤详情抽屉 */}
      <Drawer
        title="考勤记录详情"
        placement="right"
        onClose={() => setDetailDrawerVisible(false)}
        open={detailDrawerVisible}
        width={500}
      >
        {selectedRecord && (
          <div>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="学生姓名">
                {selectedRecord.student_name}
              </Descriptions.Item>
              <Descriptions.Item label="班级">
                {selectedRecord.class_name}
              </Descriptions.Item>
              <Descriptions.Item label="考勤日期">
                {moment(selectedRecord.attendance_date).format('YYYY-MM-DD dddd')}
              </Descriptions.Item>
              <Descriptions.Item label="考勤时间">
                {moment(selectedRecord.attendance_time, 'HH:mm:ss').format('HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="时段">
                {getSessionTag(selectedRecord.session_type)}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                {getStatusTag(selectedRecord.status)}
              </Descriptions.Item>
              <Descriptions.Item label="操作员">
                <Space direction="vertical" size={0}>
                  <Text>{selectedRecord.operator_name}</Text>
                  <Text code>{selectedRecord.operator_code}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="备注">
                {selectedRecord.remarks || '无'}
              </Descriptions.Item>
              <Descriptions.Item label="记录时间">
                {moment(selectedRecord.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>

      {/* 日历视图模态框 */}
      <Modal
        title="考勤日历视图"
        open={calendarVisible}
        onCancel={() => setCalendarVisible(false)}
        footer={null}
        width={800}
      >
        <Calendar
          onSelect={(date) => {
            setSelectedDate(date.format('YYYY-MM-DD'));
            // 这里可以加载指定日期的考勤数据
          }}
          dateCellRender={(value) => {
            const dateStr = value.format('YYYY-MM-DD');
            const dayRecords = records.filter(r => r.attendance_date === dateStr);
            
            if (dayRecords.length > 0) {
              return (
                <div>
                  <Badge count={dayRecords.length} size="small" />
                </div>
              );
            }
            return null;
          }}
        />
      </Modal>
    </div>
  );
};

export default ManualAttendance;
